<script setup lang="ts">
    import { columns } from '@/tables/columns/invoices';
    import { useI18n } from 'vue-i18n';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import { useQueryClient } from '@tanstack/vue-query';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import QueryKey from '@/enums/QueryKey';
    import type { Company } from '@/types';
    import { CircleAlert } from 'lucide-vue-next';

    const { t } = useI18n();

    const queryClient = useQueryClient();

    const props = defineProps<{
        company: Company;
    }>();

    const { hasPermission } = usePermissions();

    const { isPending, isFetching, isError, data, error, refetch, isPlaceholderData } = useQuery({
        queryKey: [QueryKey.DASHBOARD_INVOICES, props.company.id],
        queryFn: getInvoices,
        enabled: !!props.company.cvr,
        retry: false,
    });

    async function getInvoices() {
        const { data } = await axios.get(route('v2.dashboard.invoices.get'));
        return data;
    }
</script>

<template>
    <div
        v-if="!props.company.cvr"
        class="border-border flex flex-col items-center justify-center gap-2 rounded-md border p-4"
    >
        <CircleAlert class="text-muted-foreground" />
        <span class="text-xs">{{ t('dashboards.invoices.no_cvr') }}</span>
    </div>
    <DataTable
        v-else
        :columns="columns"
        :data="data"
        :loading="isPending"
        :default-sorting="[
            {
                id: 'booked_invoice_number',
                desc: true,
            },
        ]"
        :enable-toolbar="false"
    />
</template>
